import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { clientService } from "../services/client";
import toast from "react-hot-toast";

// Mapeamento dos dias da semana
const DIAS_SEMANA = [
  { label: "Segunda-Feira", code: "2F" },
  { label: "Terça-Feira", code: "3F" },
  { label: "Quarta-Feira", code: "4F" },
  { label: "Quinta-Feira", code: "5F" },
  { label: "Sexta-Feira", code: "6F" },
  { label: "Sábado", code: "S" },
  { label: "Domingo", code: "D" }
];

// Função para converter códigos (ex: "2F,4F,6F") para array de labels
const codigosParaDias = (codigosString) => {
  if (!codigosString || codigosString.trim() === "") return [];

  const codigos = codigosString.split(",").map(c => c.trim());
  return DIAS_SEMANA
    .filter(dia => codigos.includes(dia.code))
    .map(dia => dia.label);
};

// Função para converter array de labels para string de códigos (ex: "2F,4F,6F")
const diasParaCodigos = (diasArray) => {
  if (!Array.isArray(diasArray) || diasArray.length === 0) return "";

  return DIAS_SEMANA
    .filter(dia => diasArray.includes(dia.label))
    .map(dia => dia.code)
    .join(",");
};

const CreateClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [clientData, setClientData] = useState({
    nome: "",
    empresa: "",
    transporte: "",
    nif: "",
    zona: "",
    dias_entrega: "",
    email: "",
    data_visita: "",
    produtos: "",
    contacto: "",
    nome_contato: "",
    custo_transporte: "",
    obs: "",
    horario_entrega: "",
    packaging_veludo: "",
    preco_coracao_mozarela: "",
    descricao_faturas: "",
    isencao_custos_transporte: "",
    tipo_cliente: "",
    banner: "",
    amostra: "",
    grande_viso_2_5kgs: "",
    azul_arrabida_1_5kgs: "",
    coracao_burrata_300gr: "",
    coracao_burrata_3_5kg: "",
    coracao_burrata_500gr: "",
    flor_leite_desfiada_4_5kg: "",
    flor_leite_desfiada_400g: "",
    manteiga_cabra_500g: "",
    manteiga_vaca_500g: "",
    requeijao_500g: "",
    pack_6_veludo_cabra_fresco_160g: "",
    pack_6_veludo_cabra_130g: "",
    pack_6_serra_arrabida: "",
    grande_viso_2m_250g: "",
    azul_arrabida_250g: "",
    grande_viso_2m_5kg: "",
    grande_viso_6m_5kg: "",
    grande_viso_6m_1_5kg: "",
    pedido_mesmo_dia: false,
    usar_caixa_vaca_cabra: false,
    caixas_permitidas: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedDias, setSelectedDias] = useState([]);
  const [selectedCaixas, setSelectedCaixas] = useState([]);
  const [specificProducts, setSpecificProducts] = useState([]); // Produtos com for_all_clients=FALSE
  const [selectedSpecificProducts, setSelectedSpecificProducts] = useState([]); // Produtos específicos ativados para este cliente

  useEffect(() => {
    if (isEditing) {
      fetchClientDetails();
    }
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const data = await clientService.getClientById(id);
      setClientData({
        ...data,
        data_visita: data.data_visita ? data.data_visita.split('T')[0] : ""
      });

      // Converter códigos de dias_entrega para array de dias
      if (data.dias_entrega) {
        const dias = codigosParaDias(data.dias_entrega);
        setSelectedDias(dias);
      }

      // Converter string de caixas_permitidas para array
      if (data.caixas_permitidas) {
        setSelectedCaixas(
          data.caixas_permitidas.split(",").map((c) => c.trim()).filter(Boolean)
        );
      }

      // Carregar produtos específicos (for_all_clients=FALSE) e seu status para este cliente
      try {
        const specificProductsResponse = await clientService.getAvailableSpecificProducts(id);
        console.log("Produtos específicos carregados:", specificProductsResponse);
        setSpecificProducts(specificProductsResponse.specific_products || []);
        const enabledSpecificIds = (specificProductsResponse.specific_products || [])
          .filter(p => p.is_enabled_for_client)
          .map(p => p.id);
        setSelectedSpecificProducts(enabledSpecificIds);
      } catch (specificError) {
        console.error("Erro ao carregar produtos específicos:", specificError);
        setSpecificProducts([]);
        setSelectedSpecificProducts([]);
      }
    } catch (error) {
      toast.error(error.message || "Erro ao carregar dados do cliente");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const sanitized = type === "checkbox" ? checked : (name === "nif" ? value.replace(/\s/g, "") : value);
    setClientData((prev) => ({ ...prev, [name]: sanitized }));
  };

  const handleSpecificProductToggle = (productId) => {
    setSelectedSpecificProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleDiaToggle = (diaLabel) => {
    setSelectedDias((prev) => {
      if (prev.includes(diaLabel)) {
        return prev.filter((label) => label !== diaLabel);
      } else {
        return [...prev, diaLabel];
      }
    });
  };

  const handleCaixaToggle = (caixa) => {
    setSelectedCaixas((prev) => {
      if (prev.includes(caixa)) {
        return prev.filter((c) => c !== caixa);
      } else {
        return [...prev, caixa];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!clientData.nome.trim()) {
      setError("Nome é obrigatório");
      return;
    }

    try {
      setLoading(true);

      // Converter dias selecionados para códigos
      const diasEntregaCodigos = diasParaCodigos(selectedDias);

      // Preparar os dados para envio
      const dataToSend = {
        ...clientData,
        dias_entrega: diasEntregaCodigos,
        caixas_permitidas: clientData.usar_caixa_vaca_cabra ? selectedCaixas.join(",") : ""
      };

      let clientId = id;

      if (isEditing) {
        await clientService.updateClient(id, dataToSend);
        toast.success("Cliente atualizado com sucesso");
      } else {
        const createResponse = await clientService.createClient(dataToSend);
        clientId = createResponse.client.id;
        toast.success("Cliente criado com sucesso");
      }

      // Salvar produtos específicos ativados para este cliente
      if (selectedSpecificProducts.length > 0 && clientId) {
        try {
          await clientService.addSpecificProducts(clientId, selectedSpecificProducts);
        } catch (specificError) {
          console.error("Erro ao salvar produtos específicos:", specificError);
          toast.error("Cliente salvo, mas houve erro ao configurar produtos específicos");
        }
      }

      navigate("/clients");
    } catch (error) {
      setError(error.message || "Erro ao guardar cliente");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div className="container-fluid p-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-bold text-neutral-800 mb-8">
        {isEditing ? "Editar Cliente" : "Criar Novo Cliente"}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-md rounded-lg p-8"
      >
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Informações Básicas */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 border-b pb-2">Informações Básicas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={clientData.nome}
                  onChange={handleInputChange}
                  required
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Empresa
                </label>
                <input
                  type="text"
                  name="empresa"
                  value={clientData.empresa}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  NIF
                </label>
                <input
                  type="text"
                  name="nif"
                  value={clientData.nif}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Tipo de Cliente
                </label>
                <input
                  type="text"
                  name="tipo_cliente"
                  value={clientData.tipo_cliente}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Contacto */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 border-b pb-2">Contacto</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={clientData.email}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Contacto
                </label>
                <input
                  type="text"
                  name="contacto"
                  value={clientData.contacto}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Nome do Contato
                </label>
                <input
                  type="text"
                  name="nome_contato"
                  value={clientData.nome_contato}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Data da Visita
                </label>
                <input
                  type="date"
                  name="data_visita"
                  value={clientData.data_visita}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Localização e Entrega */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 border-b pb-2">Localização e Entrega</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Zona
                </label>
                <input
                  type="text"
                  name="zona"
                  value={clientData.zona}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Transporte
                </label>
                <input
                  type="text"
                  name="transporte"
                  value={clientData.transporte}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Dias de Entrega
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-3 border border-neutral-300 rounded-md bg-neutral-50">
                  {DIAS_SEMANA.map((dia) => (
                    <div
                      key={dia.code}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="checkbox"
                        id={`dia-${dia.code}`}
                        checked={selectedDias.includes(dia.label)}
                        onChange={() => handleDiaToggle(dia.label)}
                        className="h-4 w-4 text-neutral-900 rounded border-neutral-300 focus:ring-neutral-400"
                      />
                      <label
                        htmlFor={`dia-${dia.code}`}
                        className="text-xs sm:text-sm text-neutral-700 cursor-pointer"
                      >
                        {dia.label}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedDias.length > 0 && (
                  <p className="mt-2 text-xs text-neutral-600">
                    Selecionados: {selectedDias.join(", ")}
                    <span className="text-neutral-400 ml-2">
                      (Códigos: {diasParaCodigos(selectedDias)})
                    </span>
                  </p>
                )}
              </div>

              <div className="col-span-1 sm:col-span-2">
                <div className="flex items-center space-x-2 p-3 border border-neutral-300 rounded-md bg-neutral-50">
                  <input
                    type="checkbox"
                    id="pedido_mesmo_dia"
                    name="pedido_mesmo_dia"
                    checked={!!clientData.pedido_mesmo_dia}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-neutral-900 rounded border-neutral-300 focus:ring-neutral-400"
                  />
                  <label
                    htmlFor="pedido_mesmo_dia"
                    className="text-xs sm:text-sm text-neutral-700 cursor-pointer"
                  >
                    Faz pedido no mesmo dia?
                  </label>
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <div className="flex items-center space-x-2 p-3 border border-neutral-300 rounded-md bg-neutral-50">
                  <input
                    type="checkbox"
                    id="usar_caixa_vaca_cabra"
                    name="usar_caixa_vaca_cabra"
                    checked={!!clientData.usar_caixa_vaca_cabra}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-neutral-900 rounded border-neutral-300 focus:ring-neutral-400"
                  />
                  <label
                    htmlFor="usar_caixa_vaca_cabra"
                    className="text-xs sm:text-sm text-neutral-700 cursor-pointer"
                  >
                    Usar Caixa Vaca/Cabra
                  </label>
                </div>

                {clientData.usar_caixa_vaca_cabra && (
                  <div className="grid grid-cols-2 gap-3 p-3 mt-2 border border-neutral-300 rounded-md bg-neutral-50">
                    {["Cabra", "Vaca"].map((caixa) => (
                      <div key={caixa} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`caixa-${caixa}`}
                          checked={selectedCaixas.includes(caixa)}
                          onChange={() => handleCaixaToggle(caixa)}
                          className="h-4 w-4 text-neutral-900 rounded border-neutral-300 focus:ring-neutral-400"
                        />
                        <label
                          htmlFor={`caixa-${caixa}`}
                          className="text-xs sm:text-sm text-neutral-700 cursor-pointer"
                        >
                          {caixa}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Horário de Entrega
                </label>
                <input
                  type="text"
                  name="horario_entrega"
                  value={clientData.horario_entrega}
                  onChange={handleInputChange}
                  placeholder="Ex: 8h-18h"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Custo de Transporte
                </label>
                <input
                  type="text"
                  name="custo_transporte"
                  value={clientData.custo_transporte}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Isenção de Custos de Transporte
                </label>
                <select
                  name="isencao_custos_transporte"
                  value={clientData.isencao_custos_transporte}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                >
                  <option value="">Selecionar...</option>
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </div>
            </div>
          </div>

          {/* Produtos e Informações Comerciais */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 border-b pb-2">Produtos e Informações Comerciais</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Packaging Veludo
                </label>
                <input
                  type="text"
                  name="packaging_veludo"
                  value={clientData.packaging_veludo}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Preço Coração Mozarela
                </label>
                <input
                  type="text"
                  name="preco_coracao_mozarela"
                  value={clientData.preco_coracao_mozarela}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Banner
                </label>
                <input
                  type="text"
                  name="banner"
                  value={clientData.banner}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Amostra
                </label>
                <select
                  name="amostra"
                  value={clientData.amostra}
                  onChange={handleInputChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                >
                  <option value="">Selecionar...</option>
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Produtos
                </label>
                <textarea
                  name="produtos"
                  value={clientData.produtos}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Descrição das Faturas
                </label>
                <textarea
                  name="descricao_faturas"
                  value={clientData.descricao_faturas}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Observações
                </label>
                <textarea
                  name="obs"
                  value={clientData.obs}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Ativar Produtos para Este Cliente */}
          {isEditing && specificProducts.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-neutral-800 mb-4 border-b pb-2">
                Ativar Produtos para Este Cliente
              </h3>
              <p className="text-xs sm:text-sm text-neutral-600 mb-4">
                Estes produtos têm a opção <strong>"Produto ativo para todos os clientes"</strong> desmarcada.
                <br />
                Selecione os produtos que deseja <strong>ativar</strong> para este cliente.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {specificProducts.map((product) => (
                  <div
                    key={product.id}
                    className={`flex items-start space-x-2 p-2 border rounded-md hover:bg-neutral-50 ${
                      selectedSpecificProducts.includes(product.id)
                        ? 'border-green-300 bg-green-50'
                        : 'border-neutral-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`specific-product-${product.id}`}
                      checked={selectedSpecificProducts.includes(product.id)}
                      onChange={() => handleSpecificProductToggle(product.id)}
                      className="mt-1 h-4 w-4 text-green-600 rounded border-neutral-300 focus:ring-green-400"
                    />
                    <label
                      htmlFor={`specific-product-${product.id}`}
                      className="text-xs sm:text-sm text-neutral-700 cursor-pointer flex-1"
                    >
                      <div className="font-medium">{product.name}</div>
                      <div className="text-neutral-500">Código: {product.product_code}</div>
                    </label>
                  </div>
                ))}
              </div>

              {selectedSpecificProducts.length > 0 && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-xs sm:text-sm text-green-800">
                    <strong>{selectedSpecificProducts.length}</strong> produto(s) ativado(s) para este cliente.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 sm:mt-8 flex flex-col-reverse sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className="w-full sm:w-auto bg-neutral-100 text-neutral-900 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-200 text-xs sm:text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm disabled:opacity-50"
          >
            {loading ? "Salvando..." : isEditing ? "Atualizar Cliente" : "Criar Cliente"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateClient;